import axios from "axios";
import moment from "moment";
import { takeLatest, call, put, select, all } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { queryParams } from "../../../utils/queryParams.js";
import { stringify, stringifyParams } from "../../../utils/stringify.js";
import { API_DATE_FORMAT } from "../../../app_constants.js";

export const SET_LOADING = "saga/universal/SET_LOADING";
export const SET_SITE_FILTERS_LOADING = "saga/universal/SET_SITE_FILTERS_LOADING";
export const SET_LOCATION_FILTERS_LOADING = "saga/universal/SET_LOCATION_FILTERS_LOADING";
export const SET_FILTERS_DIRTY = "saga/universal/SET_FILTERS_DIRTY";
export const SET_TABLES_LOADING = "saga/universal/SET_TABLES_LOADING";
export const SET_REVENUE_TABLE_LOADING = "saga/universal/SET_REVENUE_TABLE_LOADING";
export const SET_DATES = "saga/universal/SET_DATES";

export const UPDATE_DATES = "saga/universal/UPDATE_DATES";
export const UPDATE_PREV_DATES = "saga/universal/UPDATE_PREV_DATES";

export const UPDATE_FILTERS = "saga/universal/UPDATE_FILTERS";
export const UPDATE_FILTERS_REQUEST = "sagas/UPDATE_FILTERS_REQUEST";
export const UPDATE_SELECTED_FIRST_LEVEL_FILTERS =
  "saga/universal/UPDATE_SELECTED_FIRST_LEVEL_FILTERS";
export const UPDATE_SELECTED_SECOND_LEVEL_FILTERS =
  "saga/universal/UPDATE_SELECTED_SECOND_LEVEL_FILTERS";
export const UPDATE_SELECTED_THIRD_LEVEL_FILTERS =
  "saga/universal/UPDATE_SELECTED_THIRD_LEVEL_FILTERS";
export const UPDATE_SELECTED_FOURTH_LEVEL_FILTERS =
  "saga/universal/UPDATE_SELECTED_FOURTH_LEVEL_FILTERS";
export const UPDATE_SITES = "saga/universal/UPDATE_SITES";

export const UPDATE_GROUP_BY = "saga/universal/UPDATE_GROUP_BY";

export const UPDATE_REVENUE_TYPE = "saga/universal/UPDATE_REVENUE_TYPE";
export const UPDATE_SECONDARY_REVENUE_TYPE = "saga/universal/UPDATE_SECONDARY_REVENUE_TYPE";

export const UPDATE_LOCATIONS = "saga/universal/UPDATE_LOCATIONS";
export const UPDATE_TABLES = "saga/universal/UPDATE_TABLES";

export const FETCH_FILTERS_SUCCESS = "saga/universal/FETCH_FILTERS_SUCCESS";
export const FETCH_FILTERS_EXCLUDE_SITES_SUCCESS =
  "saga/universal/FETCH_FILTERS_EXCLUDE_SITES_SUCCESS";
export const FETCH_FILTERS_FAILURE = "saga/universal/FETCH_FILTERS_FAILURE";

export const FETCH_TABLE_DATA = "saga/universal/FETCH_TABLE_DATA";
export const FETCH_TABLE_DATA_SUCCESS = "saga/universal/FETCH_TABLE_DATA_SUCCESS";
export const FETCH_TABLE_DATA_FAILURE = "saga/universal/FETCH_TABLE_DATA_FAILURE";

export const FETCH_CONVERSIONS = "saga/universal/FETCH_CONVERSIONS";
export const FETCH_CONVERSIONS_SUCCESS = "saga/universal/FETCH_CONVERSIONS_SUCCESS";
export const FETCH_CONVERSIONS_FAILURE = "saga/universal/FETCH_CONVERSIONS_FAILURE";

export const FETCH_ALL_REVENUE = "saga/universal/FETCH_ALL_REVENUE";
export const FETCH_ALL_REVENUE_SUCCESS = "saga/universal/FETCH_ALL_REVENUE_SUCCESS";
export const FETCH_ALL_REVENUE_FAILURE = "saga/universal/FETCH_ALL_REVENUE_FAILURE";

export const UPDATE_CUSTOM_RANGE = "sagas/UPDATE_CUSTOM_RANGE";
export const UPDATE_CUSTOM_RANGE_ACTION = "sagas/UPDATE_CUSTOM_RANGE";

export const SET_SECOND_LEVEL_FILTERS_LOADING = "sagas/SET_SECOND_LEVEL_FILTERS_LOADING";
export const SET_THIRD_LEVEL_FILTERS_LOADING = "sagas/SET_THIRD_LEVEL_FILTERS_LOADING";
export const SET_FOURTH_LEVEL_FILTERS_LOADING = "sagas/SET_FOURTH_LEVEL_FILTERS_LOADING";

export const actionCreators = {
  fetchTableData: createAction(FETCH_TABLE_DATA),
  updateDates: createAction(UPDATE_DATES),
  updatePrevDates: createAction(UPDATE_PREV_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  updateSelectedFirstLevelFilters: createAction(UPDATE_SELECTED_FIRST_LEVEL_FILTERS),
  updateSelectedSecondLevelFilters: createAction(UPDATE_SELECTED_SECOND_LEVEL_FILTERS),
  updateSelectedThirdLevelFilters: createAction(UPDATE_SELECTED_THIRD_LEVEL_FILTERS),
  updateSelectedFourthLevelFilters: createAction(UPDATE_SELECTED_FOURTH_LEVEL_FILTERS),
  updateSites: createAction(UPDATE_SITES),
  updateGroupBy: createAction(UPDATE_GROUP_BY),
  updateRevenueType: createAction(UPDATE_REVENUE_TYPE),
  updateLocations: createAction(UPDATE_LOCATIONS),
  requestNewTableData: createAction(UPDATE_TABLES),
  updateFiltersRequest: createAction(UPDATE_FILTERS),
  updateSecondaryRevenueType: createAction(UPDATE_SECONDARY_REVENUE_TYPE)
};

export default function* watchMultiLocationUniversal() {
  yield takeLatest(FETCH_TABLE_DATA, getTableData);
  yield takeLatest(UPDATE_TABLES, updateTables);
  yield takeLatest(UPDATE_FILTERS, updateFilters);
  yield takeLatest(UPDATE_SECONDARY_REVENUE_TYPE, updateRevenueTable);
}

function* getLocationsApiParams() {
  let { selectedLevelFirst, selectedLevelSecond, selectedLevelThird, selectedLevelFourth } =
    yield select((state) => state.multiLocation);

  const [firstLevelParam, secondLevelParam, thirdLevelParam, fourthLevelParam] = yield select(
    (state) => state.multiLocation.availableFilters.filter_params
  );

  return stringifyParams({
    [firstLevelParam]: selectedLevelFirst,
    [secondLevelParam]: selectedLevelSecond,
    [thirdLevelParam]: selectedLevelThird,
    [fourthLevelParam]: selectedLevelFourth
  });
}

function* getDateApiParams() {
  let {
    selectedRequestDates: { startDate, endDate },
    selectedRequestPrevDates: { prevStartDate, prevEndDate }
  } = yield select((state) => state.multiLocation);

  return {
    start_date: startDate,
    end_date: endDate,
    prev_start_date: prevStartDate,
    prev_end_date: prevEndDate
  };
}

function* getTableData(data) {
  yield put({ type: SET_LOADING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: true });

  const organization_id = yield select((state) =>
    state.commonReducer.commonProps.organization_id.toString()
  );
  const currencySymbol = yield select((state) => state.commonReducer.commonProps.currencySymbol);
  yield put({
    type: FETCH_TABLE_DATA_SUCCESS,
    payload: { ...data.payload, organization_id, currencySymbol }
  });

  const endDate = data.payload.calendarEndDate;
  const startDate = moment(endDate).startOf("month").format(API_DATE_FORMAT);

  yield put({
    type: SET_DATES,
    payload: {
      startDate: moment(endDate).startOf("month").format(API_DATE_FORMAT),
      endDate: endDate
    }
  });

  try {
    yield getFilters(organization_id);

    let params = { organization_id, start_date: startDate, end_date: endDate };

    let allSagas = [getAllRevenueData(params), getConversions(params)];

    yield all(allSagas);

    yield put({ type: SET_LOADING, payload: false });
    yield put({ type: SET_FILTERS_DIRTY, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateFilters(payload) {
  let id = yield select((state) => state.multiLocation.currentUser.organization_id);
  let organization_id = id.toString();

  if (payload.payload.name === "FilterLevelFirst") {
    yield put({
      type: UPDATE_SELECTED_FIRST_LEVEL_FILTERS,
      payload: payload.payload.data
    });

    yield put({ type: SET_SECOND_LEVEL_FILTERS_LOADING, payload: true });
    yield put({ type: SET_THIRD_LEVEL_FILTERS_LOADING, payload: true });
    yield put({ type: SET_FOURTH_LEVEL_FILTERS_LOADING, payload: true });

    yield getFiltersByFirstLevel(organization_id, payload.payload.data);
  }

  if (payload.payload.name === "FilterLevelSecond") {
    yield put({
      type: UPDATE_SELECTED_SECOND_LEVEL_FILTERS,
      payload: payload.payload.data
    });

    yield put({ type: SET_THIRD_LEVEL_FILTERS_LOADING, payload: true });
    yield put({ type: SET_FOURTH_LEVEL_FILTERS_LOADING, payload: true });

    yield getFiltersBySecondLevel(organization_id, payload.payload.data);
  }

  if (payload.payload.name === "FilterLevelThird") {
    yield put({
      type: UPDATE_SELECTED_THIRD_LEVEL_FILTERS,
      payload: payload.payload.data
    });

    yield put({ type: SET_FOURTH_LEVEL_FILTERS_LOADING, payload: true });
    yield getFiltersByThirdLevel(organization_id, payload.payload.data);
  }
}

function* updateTables() {
  yield put({ type: SET_TABLES_LOADING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: false });

  let organization_id = yield select((state) => state.multiLocation.currentUser.organization_id);

  let dates = yield getDateApiParams();

  let params = { organization_id: organization_id.toString(), ...dates };

  let allSagas = [getAllRevenueData(params), getConversions(params)];

  try {
    yield all(allSagas);
    yield put({ type: SET_TABLES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateRevenueTable() {
  yield put({ type: SET_REVENUE_TABLE_LOADING, payload: true });

  let organization_id = yield select((state) => state.commonReducer.commonProps.organization_id);

  let dates = yield getDateApiParams();

  let params = { organization_id: organization_id.toString(), ...dates };

  try {
    yield getAllRevenueData(params);
    yield put({ type: SET_REVENUE_TABLE_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* getConversions(params) {
  let locationParams = yield getLocationsApiParams();
  const group_by = yield select((state) => state.multiLocation.groupBy.value.id);

  try {
    const response = yield call(
      axios.post,
      queryParams.apiVersion + "reports/multilocation_page/get_conversions",
      { ...params, ...locationParams, group_by }
    );
    if (!response.data) {
      return;
    }

    yield put({ type: FETCH_CONVERSIONS_SUCCESS, payload: response.data });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_CONVERSIONS_FAILURE, error });
  }
}

function* getAllRevenueData(params) {
  let locationParams = yield getLocationsApiParams();
  const group_by = yield select((state) => state.multiLocation.groupBy.value.id);
  const revenue_type = yield select((state) => state.multiLocation.revenueType.value.id);
  const revenue_scope = yield select((state) => state.multiLocation.secondaryRevenueType.value.id);

  try {
    const response = yield call(
      axios.post,
      queryParams.apiVersion + "reports/multilocation_page/get_revenues",
      { ...params, ...locationParams, group_by, revenue_type, revenue_scope }
    );

    if (!response.data) {
      return;
    }

    yield put({ type: FETCH_ALL_REVENUE_SUCCESS, payload: response.data });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_ALL_REVENUE_FAILURE, error });
  }
}

function* getFilters(organization_id) {
  try {
    const response = yield call(
      axios.post,
      queryParams.apiVersion + "reports/multilocation_page/get_filters",
      { organization_id }
    );

    if (!response.data) {
      return;
    }

    yield put({ type: FETCH_FILTERS_SUCCESS, payload: response.data });
  } catch (error) {
    console.log("fetch filters error:", error);
    yield put({ type: FETCH_FILTERS_FAILURE, error });
  }
}

function* getFiltersByFirstLevel(organization_id, payload) {
  const firstLevelParam = yield select(
    (state) => state.multiLocation.availableFilters.filter_params[0]
  );
  try {
    const response = yield call(
      axios.post,
      queryParams.apiVersion + "reports/multilocation_page/get_filters",
      {
        organization_id,
        [firstLevelParam]: stringify(payload)
      }
    );
    if (!response.data) {
      return;
    }

    yield put({ type: UPDATE_FILTERS_REQUEST, payload: response.data.filters });
    yield put({
      type: UPDATE_SELECTED_SECOND_LEVEL_FILTERS,
      payload: response.data.filters.level_2
    });
    yield put({
      type: UPDATE_SELECTED_THIRD_LEVEL_FILTERS,
      payload: response.data.filters.level_3
    });
    yield put({
      type: UPDATE_SELECTED_FOURTH_LEVEL_FILTERS,
      payload: response.data.filters.level_4
    });
    yield put({ type: SET_SECOND_LEVEL_FILTERS_LOADING, payload: false });
    yield put({ type: SET_THIRD_LEVEL_FILTERS_LOADING, payload: false });
    yield put({ type: SET_FOURTH_LEVEL_FILTERS_LOADING, payload: false });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_FILTERS_FAILURE, error });
  }
}

function* getFiltersBySecondLevel(organization_id, payload) {
  const selectedFirstLevel = yield select((state) => state.multiLocation.selectedLevelFirst);

  const [firstLevelParam, secondLevelParam] = yield select(
    (state) => state.multiLocation.availableFilters.filter_params
  );

  try {
    const response = yield call(
      axios.post,
      queryParams.apiVersion + "reports/multilocation_page/get_filters",
      {
        organization_id,
        [firstLevelParam]: stringify(selectedFirstLevel),
        [secondLevelParam]: stringify(payload)
      }
    );
    if (!response.data) {
      return;
    }

    yield put({ type: UPDATE_FILTERS_REQUEST, payload: response.data.filters });
    yield put({
      type: UPDATE_SELECTED_THIRD_LEVEL_FILTERS,
      payload: response.data.filters.level_3
    });
    yield put({
      type: UPDATE_SELECTED_FOURTH_LEVEL_FILTERS,
      payload: response.data.filters.level_4
    });
    yield put({ type: SET_THIRD_LEVEL_FILTERS_LOADING, payload: false });
    yield put({ type: SET_FOURTH_LEVEL_FILTERS_LOADING, payload: false });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_FILTERS_FAILURE, error });
  }
}

function* getFiltersByThirdLevel(organization_id, payload) {
  const selectedFirstLevel = yield select((state) => state.multiLocation.selectedLevelFirst);

  const selectedSecondLevel = yield select((state) => state.multiLocation.selectedLevelSecond);

  const [firstLevelParam, secondLevelParam, thirdLevelParam] = yield select(
    (state) => state.multiLocation.availableFilters.filter_params
  );

  try {
    const response = yield call(
      axios.post,
      queryParams.apiVersion + "reports/multilocation_page/get_filters",
      {
        organization_id,
        [firstLevelParam]: stringify(selectedFirstLevel),
        [secondLevelParam]: stringify(selectedSecondLevel),
        [thirdLevelParam]: stringify(payload)
      }
    );
    if (!response.data) {
      return;
    }

    yield put({ type: UPDATE_FILTERS_REQUEST, payload: response.data.filters });
    yield put({
      type: UPDATE_SELECTED_FOURTH_LEVEL_FILTERS,
      payload: response.data.filters.level_4
    });
    yield put({ type: SET_FOURTH_LEVEL_FILTERS_LOADING, payload: false });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_FILTERS_FAILURE, error });
  }
}
